/**
 * dashboard/classes/index.tsx — Tutor's class list.
 *
 * Lives at `/dashboard/classes`. The horizontal-scroll ClassWall is the
 * signature element of this surface; we reuse it here (with empty-state
 * copy that nudges new tutors to create their first class).
 *
 * Also pre-fetches `classesQuery` so the rest of the dashboard tree reads
 * the cached result without an extra round-trip.
 */

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { ClassWall } from '../../../components/tutor/ClassWall.js';
import {
  classesQuery,
  freshSessionQuery,
  type ClassroomSummary,
  type SessionPrincipal,
} from '../../../lib/api/queries.js';

export const Route = createFileRoute('/dashboard/classes/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(classesQuery);
    return null;
  },
  component: ClassesListPage,
});

function ClassesListPage(): ReactNode {
  const { queryClient } = Route.useRouteContext();
  const cached = queryClient.getQueryData(classesQuery.queryKey) as
    { ok?: boolean; data?: { classes: ClassroomSummary[] } } | undefined;
  const classes: ClassroomSummary[] = cached?.ok ? (cached.data?.classes ?? []) : [];

  // Map the API shape onto the ClassWall prop shape (which expects a richer
  // card surface — placeholder values for fields the API doesn't yet expose).
  const cards = classes.map((cls) => ({
    id: cls.id,
    name: cls.name,
    meta: `SD Kelas ${cls.sdGrade}`,
    recent: false,
    completedLevels: 0,
    totalLevels: 5,
  }));

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Kelas"
        title="Daftar kelas"
        description="Semua kelas yang kamu buka. Klik kartu kelas untuk melihat detail, mengundang siswa, dan mengelola materi."
        action={
          <Link className="button button-primary" to="/dashboard/classes/new">
            Buka kelas baru
          </Link>
        }
      />

      <section className="section" aria-labelledby="kelas-anda">
        <h2 className="section-title" id="kelas-anda">
          Kelas Anda
        </h2>
        <ClassWall
          classes={cards}
          emptyTitle="Belum ada kelas"
          emptyDescription="Buat kelas pertama kamu untuk mulai mengelola siswa dan materi."
          emptyHref="/dashboard/classes/new"
          emptyLabel="Buat kelas"
        />
      </section>

      <section className="section" aria-labelledby="archive-section">
        <h2 className="section-title" id="archive-section">
          Riwayat
        </h2>
        <StatePanel
          variant="disabled"
          title="Riwayat kelas akan tersedia"
          description="Kelas yang sudah ditutup akan tampil di sini untuk arsip."
        />
      </section>
    </main>
  );
}
